/**
 * The provider's suggested panels, made drawable (SD-2.5, FR-6, UX-DR8, DESIGN.md §4).
 *
 * WHAT "ACTIVE" MEANS IN B0, decided here and nowhere else: a panel is active when its plane
 * renders SUNLIT, using `faceIsShaded` — the same AD-11 shade derate the face fills use. The
 * story says panels open "already placed on the best faces", and the best faces this surface can
 * defend are the ones it already draws as sunlit. One definition of shaded, not two: a panel
 * drawn dark-and-active on a face drawn shaded would be the canvas disagreeing with itself.
 * Everything else is a GHOST — an available position, dashed, discoverable (UX-DR8), and
 * excluded from the summary's counts. Epic 4 replaces this rule with the rep's own toggles and
 * the target-fill packer; nothing downstream may persist this split.
 *
 * PLAN-VIEW FORESHORTENING. The panel lies on the pitched plane; the canvas is a plan view. The
 * up-slope dimension therefore renders at cos(pitch) of its true length. The provider spaced the
 * panel CENTRES in plan, so skipping this draws full-length modules over plan-spaced rows and
 * overlaps every steep roof — a defect invisible on the flat mockup and obvious on Oak Park's
 * 45-to-65 degree planes.
 *
 * THE MODULE CONSTANT. roof_model_v1 carries no module dimensions or wattage, and FR-27's
 * module library is Epic 4 (SD-4.4). Until then B0 draws and sizes with ONE named constant.
 * The 1.7 m long side is pinned by DESIGN.md §4's own scale note ("a 1.7 m module renders
 * 46 px"); 400 W is the residential default the seeded library will refine. SD-4.4 replaces
 * this constant with the tenant's selected module — one import site, not a scattering.
 */

import type { RoofModelV1 } from '@/types/solar/roofModel';
import {
  faceIsShaded,
  polygonToPath,
  sunshineDenominator,
  type Segment,
} from './canvasGeometry';
import { enuToSvg } from './canvasViewport';
import type { PanelGridRef } from './panelToggle';

export const DEFAULT_MODULE = Object.freeze({
  /** DESIGN.md §4 scale note. */
  longM: 1.7,
  shortM: 1.0,
  /** FR-27's wattage, B0 stand-in until SD-4.4's pv_modules drives it. */
  watts: 400,
} as const);

/**
 * The physical module driving panel size and wattage (SD-4.4, FR-27). `DEFAULT_MODULE` is the
 * shape's fallback value: it applies when a tenant has chosen no default, so every geometry
 * function takes `module = DEFAULT_MODULE` and nothing regresses without a selection.
 */
export interface PanelModule {
  longM: number;
  shortM: number;
  watts: number;
}

/**
 * A pv_modules row, converted. Datasheet convention (pinned in the 20260805g migration):
 * `height_mm` is the LONG side, `width_mm` the short. Exact division — the mm figures are
 * integers precisely so this conversion introduces nothing.
 */
export function panelModuleFromRow(row: {
  watts: number;
  width_mm: number;
  height_mm: number;
}): PanelModule {
  return { longM: row.height_mm / 1000, shortM: row.width_mm / 1000, watts: row.watts };
}

type Orientation = RoofModelV1['suggested_panels'][number]['orientation'];

const rad = (deg: number): number => (deg * Math.PI) / 180;
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The plan-view footprint of one panel, as a 4-vertex polygon in ENU metres.
 *
 * `azimuthDeg` is the plane's downslope compass bearing (clockwise from true north), so the
 * panel's slope-aligned axis points along `(sin az, cos az)` in ENU. Portrait lays the module's
 * long side up-slope; landscape lays it across. Only the up-slope dimension foreshortens.
 */
export function panelPolygonM(
  centerM: readonly [number, number],
  orientation: Orientation,
  pitchDeg: number,
  azimuthDeg: number,
  module: PanelModule = DEFAULT_MODULE
): [number, number][] {
  const [cx, cy] = centerM;
  const az = rad(azimuthDeg);
  // Downslope unit vector and its cross-slope perpendicular, in ENU (x east, y north).
  const ux = Math.sin(az);
  const uy = Math.cos(az);
  const vx = uy;
  const vy = -ux;

  const alongSlopeM = orientation === 'portrait' ? module.longM : module.shortM;
  const acrossSlopeM = orientation === 'portrait' ? module.shortM : module.longM;
  const halfAlong = (alongSlopeM * Math.cos(rad(pitchDeg))) / 2;
  const halfAcross = acrossSlopeM / 2;

  return [
    [cx - vx * halfAcross - ux * halfAlong, cy - vy * halfAcross - uy * halfAlong],
    [cx + vx * halfAcross - ux * halfAlong, cy + vy * halfAcross - uy * halfAlong],
    [cx + vx * halfAcross + ux * halfAlong, cy + vy * halfAcross + uy * halfAlong],
    [cx - vx * halfAcross + ux * halfAlong, cy - vy * halfAcross + uy * halfAlong],
  ];
}

export interface DrawablePanel {
  /** Stable per model: the panel's index in `suggested_panels`. */
  key: string;
  planeId: string;
  active: boolean;
  /** Closed SVG path in user units, same convention as `DrawableFace.path`. */
  path: string;
  /** DESIGN.md §4 "inner division lines for module texture". Active treatment only. */
  divisions: Segment[];
  /**
   * SD-4.1: the grid coordinate a rep's toggle is stored against, set by `packedLayout`.
   *
   * ABSENT ON PROVIDER PANELS, and that absence is load-bearing rather than an omission. A
   * provider panel is keyed by its index in `suggested_panels`, which is not stable across a
   * recompute, so it has no identity a persisted decision could bind to. Panels without a grid are
   * therefore not offered as clickable and `applyPanelDecisions` leaves them alone — the rule is
   * structural, not a convention someone has to remember.
   */
  grid?: PanelGridRef;
}

/**
 * Division lines: two, splitting the long axis into three equal cells, each spanning the short
 * axis. Ghosts carry none — texture on a dashed 7% fill is noise, and DESIGN.md specifies the
 * lines on the active treatment only.
 */
function divisionSegments(
  centerM: readonly [number, number],
  orientation: Orientation,
  pitchDeg: number,
  azimuthDeg: number,
  module: PanelModule
): Segment[] {
  const [cx, cy] = centerM;
  const az = rad(azimuthDeg);
  const ux = Math.sin(az);
  const uy = Math.cos(az);
  const vx = uy;
  const vy = -ux;

  const cosP = Math.cos(rad(pitchDeg));
  // The LONG axis is where the cells divide. Its plan length, and the short axis' plan length,
  // depend on which of the two lies up-slope.
  const longAxis: [number, number] =
    orientation === 'portrait' ? [ux * cosP, uy * cosP] : [vx, vy];
  const shortAxis: [number, number] =
    orientation === 'portrait' ? [vx, vy] : [ux * cosP, uy * cosP];
  const longPlanM = module.longM;
  const shortPlanM = module.shortM;

  const out: Segment[] = [];
  for (const t of [-1 / 6, 1 / 6]) {
    const ox = cx + longAxis[0] * longPlanM * t;
    const oy = cy + longAxis[1] * longPlanM * t;
    const a = enuToSvg([ox - (shortAxis[0] * shortPlanM) / 2, oy - (shortAxis[1] * shortPlanM) / 2]);
    const b = enuToSvg([ox + (shortAxis[0] * shortPlanM) / 2, oy + (shortAxis[1] * shortPlanM) / 2]);
    out.push({ x1: round2(a.x), y1: round2(a.y), x2: round2(b.x), y2: round2(b.y) });
  }
  return out;
}

/**
 * Every suggested panel, drawable, computed once and memoisable by the component.
 *
 * A panel whose `plane_id` is not in the model (the provider suggests panels for segments the
 * segmentation later dropped) renders as a GHOST with no rotation: an available position is an
 * honest claim without a face; an active panel floating on bare imagery is not. A non-finite
 * centre drops the panel deliberately, mirroring the SD-2.3 face rule: missing is visible and
 * countable, reshaped is not.
 */
export function drawablePanels(
  model: RoofModelV1 | null | undefined,
  module: PanelModule = DEFAULT_MODULE
): DrawablePanel[] {
  if (!model?.suggested_panels?.length) return [];
  const planes = new Map(model.planes.map((p) => [p.id, p]));
  // SD-3.2: the AD-11 denominator, which falls back to the roof's own planes when the site
  // ceiling is absent. Passing the raw ceiling drew every panel active on such roofs.
  const ceiling = sunshineDenominator(model).value;

  const out: DrawablePanel[] = [];
  for (let i = 0; i < model.suggested_panels.length; i += 1) {
    const suggested = model.suggested_panels[i];
    const plane = planes.get(suggested.plane_id);
    const pitch = plane?.pitch_deg ?? 0;
    const azimuth = plane?.azimuth_deg ?? 0;
    const active = plane ? !faceIsShaded(plane, ceiling) : false;

    const path = polygonToPath(
      panelPolygonM(suggested.center_m, suggested.orientation, pitch, azimuth, module)
    );
    if (!path) continue;

    out.push({
      key: `panel-${i}`,
      planeId: suggested.plane_id,
      active,
      path,
      divisions: active
        ? divisionSegments(suggested.center_m, suggested.orientation, pitch, azimuth, module)
        : [],
    });
  }
  return out;
}

export interface PanelSummary {
  totalPositions: number;
  activeCount: number;
  unusedCount: number;
  /** activeCount × module watts / 1000. The module defaults to DEFAULT_MODULE. */
  kwDc: number;
}

/** The counts the summary shell shows. Ghosts are positions, not system size. */
export function panelSummary(
  model: RoofModelV1 | null | undefined,
  module: PanelModule = DEFAULT_MODULE
): PanelSummary | null {
  if (!model) return null;
  const panels = drawablePanels(model, module);
  const activeCount = panels.filter((p) => p.active).length;
  return {
    totalPositions: panels.length,
    activeCount,
    unusedCount: panels.length - activeCount,
    kwDc: (activeCount * module.watts) / 1000,
  };
}
